/**
 * AlertDialog — Accessible confirmation dialog
 *
 * Wraps Radix UI AlertDialog primitives with ButterUI styling.
 * Used for destructive or irreversible actions that require explicit user confirmation.
 *
 * Provides focus trap, backdrop overlay, Escape-to-close, and proper ARIA roles
 * out of the box via Radix.
 *
 * @example
 * ```tsx
 * <AlertDialog open={open} onOpenChange={setOpen}>
 *   <AlertDialogTrigger asChild>
 *     <Button variant="destructive">Delete</Button>
 *   </AlertDialogTrigger>
 *   <AlertDialogContent>
 *     <AlertDialogHeader>
 *       <AlertDialogTitle>Are you sure?</AlertDialogTitle>
 *       <AlertDialogDescription>This cannot be undone.</AlertDialogDescription>
 *     </AlertDialogHeader>
 *     <AlertDialogFooter>
 *       <AlertDialogCancel>Go Back</AlertDialogCancel>
 *       <AlertDialogAction>Confirm</AlertDialogAction>
 *     </AlertDialogFooter>
 *   </AlertDialogContent>
 * </AlertDialog>
 * ```
 *
 * @see https://www.radix-ui.com/primitives/docs/components/alert-dialog
 * @see https://www.w3.org/WAI/ARIA/apg/patterns/alertdialog/
 */
import type { ComponentRef, Ref, ComponentPropsWithoutRef, HTMLAttributes } from 'react'
import * as AlertDialogPrimitive from '@radix-ui/react-alert-dialog'
import { cn } from '#utils'

// ─── Root & Trigger (no styling needed) ──────────────────────────────

const AlertDialog = AlertDialogPrimitive.Root
const AlertDialogTrigger = AlertDialogPrimitive.Trigger
const AlertDialogPortal = AlertDialogPrimitive.Portal

// ─── Overlay ─────────────────────────────────────────────────────────

function AlertDialogOverlay({
  className,
  ref,
  ...props
}: ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Overlay> & {
  ref?: Ref<ComponentRef<typeof AlertDialogPrimitive.Overlay>>
}) {
  return (
    <AlertDialogPrimitive.Overlay
      ref={ref}
      className={cn(
        'fixed inset-0 z-50 bg-black/40',
        'data-[state=open]:animate-in data-[state=closed]:animate-out',
        'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
        className,
      )}
      {...props}
    />
  )
}

// ─── Content ─────────────────────────────────────────────────────────

function AlertDialogContent({
  className,
  ref,
  ...props
}: ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Content> & {
  ref?: Ref<ComponentRef<typeof AlertDialogPrimitive.Content>>
}) {
  return (
    <AlertDialogPortal>
      <AlertDialogOverlay />
      <AlertDialogPrimitive.Content
        ref={ref}
        className={cn(
          // Positioning. Width leaves a gutter either side so the dialog reads
          // as a card on a phone rather than running edge to edge; `max-w-md`
          // still caps it once there's room.
          'fixed left-1/2 top-1/2 z-50 w-dialog-inset max-w-md -translate-x-1/2 -translate-y-1/2',
          // Visual
          'rounded-2xl border border-gray-200 bg-white p-6 shadow-xl',
          // Animations
          'duration-200',
          'data-[state=open]:animate-in data-[state=closed]:animate-out',
          'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
          'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
          'data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%]',
          'data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]',
          className,
        )}
        {...props}
      />
    </AlertDialogPortal>
  )
}

// ─── Header / Footer (layout helpers) ────────────────────────────────

function AlertDialogHeader({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('flex flex-col gap-2', className)} {...props} />
}

function AlertDialogFooter({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn('mt-6 flex items-center justify-end gap-3', className)}
      {...props}
    />
  )
}

// ─── Title & Description ─────────────────────────────────────────────

function AlertDialogTitle({
  className,
  ref,
  ...props
}: ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Title> & {
  ref?: Ref<ComponentRef<typeof AlertDialogPrimitive.Title>>
}) {
  return (
    <AlertDialogPrimitive.Title
      ref={ref}
      className={cn('text-lg font-semibold text-gray-900', className)}
      {...props}
    />
  )
}

function AlertDialogDescription({
  className,
  ref,
  ...props
}: ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Description> & {
  ref?: Ref<ComponentRef<typeof AlertDialogPrimitive.Description>>
}) {
  return (
    <AlertDialogPrimitive.Description
      ref={ref}
      className={cn('text-sm text-gray-500', className)}
      {...props}
    />
  )
}

// ─── Action & Cancel buttons ─────────────────────────────────────────

function AlertDialogAction({
  className,
  ref,
  ...props
}: ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Action> & {
  ref?: Ref<ComponentRef<typeof AlertDialogPrimitive.Action>>
}) {
  return (
    <AlertDialogPrimitive.Action
      ref={ref}
      className={cn(
        'inline-flex items-center justify-center rounded-full px-5 py-2.5',
        'text-sm font-semibold transition-colors',
        'focus:outline-none focus:ring-2 focus:ring-offset-2',
        'disabled:cursor-not-allowed disabled:opacity-50',
        // Default: brand button (consumers can override via className for destructive)
        'bg-butter-brand-600 text-white hover:bg-butter-brand-700 focus:ring-butter-brand-600',
        className,
      )}
      {...props}
    />
  )
}

function AlertDialogCancel({
  className,
  ref,
  ...props
}: ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Cancel> & {
  ref?: Ref<ComponentRef<typeof AlertDialogPrimitive.Cancel>>
}) {
  return (
    <AlertDialogPrimitive.Cancel
      ref={ref}
      className={cn(
        'inline-flex items-center justify-center rounded-full px-5 py-2.5',
        'text-sm font-medium text-gray-700 transition-colors',
        'hover:bg-gray-100',
        'focus:outline-none focus:ring-2 focus:ring-gray-300 focus:ring-offset-2',
        'disabled:cursor-not-allowed disabled:opacity-50',
        className,
      )}
      {...props}
    />
  )
}

export {
  AlertDialog,
  AlertDialogTrigger,
  AlertDialogPortal,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
}
